import { Action, createReducer, on } from '@ngrx/store'
import * as UserActions from './user.actions'
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { User } from '../components/shared/model/user.model'

export const USER_FEATURE_KEY = 'user'

export interface UserState extends EntityState<User> {
  currentUser: User | null
  totalCount: number
  loading: boolean
  error: string | null
}

export interface UserPartialState {
  readonly [USER_FEATURE_KEY]: UserState
}

export const userAdapter: EntityAdapter<User> = createEntityAdapter<User>()

export const initialState: UserState = userAdapter.getInitialState({
  currentUser: null,
  totalCount: 0,
  loading: false,
  error: null,
})

const reducer = createReducer(
  initialState,
  on(UserActions.loadUsers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.loadUsersSuccess, (state, { users, totalCount }) =>
    userAdapter.setAll(users, {
      ...state,
      totalCount,
      loading: false,
      error: null,
    })
  ),
  on(UserActions.loadUsersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(UserActions.createUserSuccess, (state, { user }) =>
    userAdapter.addOne(user, {
      ...state,
      totalCount: state.totalCount + 1,
      error: null,
    })
  ),
  on(UserActions.createUserFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(UserActions.updateUserSuccess, (state, { id, user }) =>
    userAdapter.updateOne(
      { id, changes: user },
      {
        ...state,
        error: null,
      }
    )
  ),
  on(UserActions.updateUserFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(UserActions.deleteUserSuccess, (state, { id }) =>
    userAdapter.removeOne(id, {
      ...state,
      totalCount: state.totalCount - 1,
      error: null,
    })
  ),
  on(UserActions.deleteUserFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(UserActions.loadCurrentUser, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.loadCurrentUserSuccess, (state, { user }) => ({
    ...state,
    currentUser: user,
    loading: false,
  })),
  on(UserActions.loadCurrentUserFailure, (state, { error }) => ({
    ...state,
    currentUser: null,
    loading: false,
    error,
  })),
  on(UserActions.clearCurrentUser, (state) => ({
    ...state,
    currentUser: null,
    error: null,
    loading: false,
  }))
)

function normalizeUserState(state: UserState | undefined): UserState {
  if (!state) {
    return initialState
  }

  return {
    ...initialState,
    ...state,
    ids: state.ids ?? initialState.ids,
    entities: state.entities ?? initialState.entities,
    currentUser: state.currentUser ?? null,
    totalCount: state.totalCount ?? 0,
    loading: state.loading ?? false,
    error: state.error ?? null,
  }
}

export function userReducer(state: UserState | undefined, action: Action) {
  return reducer(normalizeUserState(state), action)
}
